/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import Iframe from 'react-iframe';

// @mui material components
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDSelect from 'components/MDSelect';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import MDTypography from 'components/MDTypography';
import LocationProduct from './LocationProduct';

const initAttr = {
  id: '',
  code: '',
  barcode: '',
  branch_id: '',
  warehouse_id: '',
  zone_id: '',
  shelf_id: '',
  floor: '',
  block: '',
  zone_code: '',
  shelf_code: '',
  floor_code: '',
  block_code: '',
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

const initFind = {
  search: '',
  page: 1,
  perPage: 18,
  pageTotal: 0,
  branch: 'ALL',
  warehouse: 'ALL',
  zone: 'ALL',
};

function Location() {
  const mod = 'location';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [modalDel, setModalDel] = useState(false);
  const [modalPrint, setModalPrint] = useState(false);
  const [cboBranch, setCboBranch] = useState([]);
  const [cboWarehouse, setCboWarehouse] = useState([]);
  const [cboZone, setCboZone] = useState([]);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      branch: data.branch,
      warehouse: data.warehouse,
      zone: data.zone,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const refresh = () => {
    view(find);
  };

  const print = (item) => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/print`, app.token, {
      id: item.id,
      lang: app.lang,
    }).then((result) => {
      setLoading(false);
      if (result.status === 'ok') {
        setAttr({ ...item, print: result.data.print });
        setModalPrint(true);
      }
    });
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.warehouse_id, 1)) return;
    if (!Func.verifyLength(attr.zone_code, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setLoading(true);
    setModalAdd(false);
    setConfirm(false);

    FetchData('PUT', `/${mod}`, app.token, {
      enable: attr.enable,
      warehouse_id: attr.warehouse_id,
      zone_code: attr.zone_code.toUpperCase(),
      shelf_code: attr.shelf_code.toUpperCase(),
      floor_code: attr.floor_code.toUpperCase(),
      block_code: attr.block_code.toUpperCase(),
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        setModalAdd(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editConfirm = () => {
    if (!Func.verifyLength(attr.branch_id, 1)) return;

    setConfirm(true);
  };

  const edit = (item) => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('PATCH', `/${mod}/${item.id}`, app.token, item).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view(find);
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.saveerror`), 'error');
          }
        });
      }
    });
  };

  const del = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view(find);
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 18 })}>
        {t(`element.show_record`, { num: 18 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 48 })}>
        {t(`element.show_record`, { num: 48 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 500 })}>
        {t(`element.show_record`, { num: 500 })}
      </MenuItem>
    </Menu>
  );

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(initAttr);
        setCboBranch(result.branch);
        setCboWarehouse(result.warehouse);
        setCboZone(result.zone);
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const btnLocationReceive = (item) => {
    if (item.location_receive === 'Y')
      return (
        <Tooltip title='ตำแหน่งรับเข้า'>
          <IconButton
            aria-label='Receive'
            color='primary'
            size='small'
            onClick={(e) => {
              edit({
                id: item.id,
                mode: 'receive',
                location_receive: 'N',
              });
            }}
          >
            <Icon>move_to_inbox</Icon>
          </IconButton>
        </Tooltip>
      );

    return (
      <Tooltip title='ตำแหน่งรับเข้า'>
        <IconButton
          aria-label='Receive'
          size='small'
          onClick={(e) => {
            edit({
              id: item.id,
              mode: 'receive',
              location_receive: 'Y',
            });
          }}
        >
          <Icon>move_to_inbox</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const btnLocationDelivery = (item) => {
    if (item.location_delivery === 'Y')
      return (
        <Tooltip title='ตำแหน่งรอส่ง'>
          <IconButton
            aria-label='Export'
            color='success'
            size='small'
            onClick={(e) => {
              edit({
                id: item.id,
                mode: 'delivery',
                location_delivery: 'N',
              });
            }}
          >
            <Icon>outbox</Icon>
          </IconButton>
        </Tooltip>
      );

    return (
      <Tooltip title='ตำแหน่งรอส่ง'>
        <IconButton
          aria-label='Export'
          size='small'
          onClick={(e) => {
            edit({
              id: item.id,
              mode: 'delivery',
              location_delivery: 'Y',
            });
          }}
        >
          <Icon>outbox</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const btnLocationProduction = (item) => {
    if (item.location_production === 'Y')
      return (
        <Tooltip title='ตำแหน่งรับผลิต'>
          <IconButton
            aria-label='Production'
            color='info'
            size='small'
            onClick={(e) => {
              edit({
                id: item.id,
                mode: 'production',
                location_production: 'N',
              });
            }}
          >
            <Icon>precision_manufacturing</Icon>
          </IconButton>
        </Tooltip>
      );

    return (
      <Tooltip title='ตำแหน่งรับผลิต'>
        <IconButton
          aria-label='Production'
          size='small'
          onClick={(e) => {
            edit({
              id: item.id,
              mode: 'production',
              location_production: 'Y',
            });
          }}
        >
          <Icon>precision_manufacturing</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const btnRawmat = (item) => {
    if (item.rawmat === 'Y')
      return (
        <Tooltip title='ตำแหน่งวัตถุดิบ'>
          <IconButton
            aria-label='rawmat'
            color='warning'
            size='small'
            onClick={(e) => {
              edit({
                id: item.id,
                mode: 'rawmat',
                rawmat: 'N',
              });
            }}
          >
            <Icon>settings_input_composite</Icon>
          </IconButton>
        </Tooltip>
      );

    return (
      <Tooltip title='ตำแหน่งวัตถุดิบ'>
        <IconButton
          aria-label='rawmat'
          size='small'
          onClick={(e) => {
            edit({
              id: item.id,
              mode: 'rawmat',
              rawmat: 'Y',
            });
          }}
        >
          <Icon>settings_input_composite</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const btnDel = (item) => {
    return (
      <Tooltip title='ลบ'>
        <IconButton
          aria-label='delete'
          size='small'
          onClick={() => del(item)}
          sx={{ marginLeft: 'auto' }}
        >
          <Icon>delete</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
            >
              <MenuItem value='ALL'>สาขา</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
            <MDInput
              fullWidth
              value={find.warehouse}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, warehouse: e.target.value, page: 1 })}
            >
              <MenuItem value='ALL'>คลัง</MenuItem>
              {cboWarehouse.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    const subHeader = (row) => {
      if (row.product_code === '')
        return <MDTypography variant='caption'>ไม่มีสินค้า</MDTypography>;

      return <MDTypography variant='caption'>{row.product_code}</MDTypography>;
    };

    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              onClick={() => {
                setAttr(initAttr);
                setConfirm(false);
                setModalAdd(true);
              }}
            >
              <Icon>post_add</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {record.map((row) => (
                <Grid item xs={12} sm={6} md={3} lg={2} key={row.id}>
                  <Card sx={{ overflow: 'hidden' }}>
                    <CardHeader
                      avatar={<LocationProduct callback={refresh} parent={row} />}
                      title={row.code}
                      subheader={subHeader(row)}
                      action={
                        <Tooltip title='พิมพ์ตำแหน่ง'>
                          <IconButton aria-label='print' color='dark' onClick={() => print(row)}>
                            <Icon>local_printshop</Icon>
                          </IconButton>
                        </Tooltip>
                      }
                    />
                    <CardContent sx={{ py: 0 }}>
                      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                        <Icon>store</Icon> {row.warehouse_name}
                      </Typography>
                      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                        <Icon>storefront</Icon> {row.branch_name}
                      </Typography>
                    </CardContent>
                    <CardActions disableSpacing>
                      {btnLocationReceive(row)}
                      {btnLocationDelivery(row)}
                      {btnLocationProduction(row)}
                      {btnRawmat(row)}
                      {btnDel(row)}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDSelect
            label='คลัง *'
            labelDefault={t(`element.please_select`)}
            data={cboWarehouse}
            value={attr.warehouse_id}
            onChange={(e) => setAttr({ ...attr, warehouse_id: e.target.value })}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <MDInput
            label='โซน'
            onChange={(e) => setAttr({ ...attr, zone_code: e.target.value })}
            value={attr.zone_code}
            variant='standard'
            fullWidth
            inputProps={{
              maxLength: 3,
              autoComplete: 'off',
              sx: { textTransform: 'uppercase' },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <MDInput
            label='ชั้นวาง'
            onChange={(e) => setAttr({ ...attr, shelf_code: e.target.value })}
            value={attr.shelf_code}
            variant='standard'
            fullWidth
            inputProps={{
              maxLength: 3,
              autoComplete: 'off',
              sx: { textTransform: 'uppercase' },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <MDInput
            label='ชั้นที่'
            onChange={(e) => setAttr({ ...attr, floor_code: e.target.value })}
            value={attr.floor_code}
            variant='standard'
            fullWidth
            inputProps={{
              maxLength: 3,
              autoComplete: 'off',
              sx: { textTransform: 'uppercase' },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <MDInput
            label='Block'
            onChange={(e) => setAttr({ ...attr, block_code: e.target.value })}
            value={attr.block_code}
            variant='standard'
            fullWidth
            inputProps={{
              maxLength: 3,
              autoComplete: 'off',
              sx: { textTransform: 'uppercase' },
            }}
          />
        </Grid>
      </Grid>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth='xs' keepMounted aria-describedby='dialog-add' fullWidth>
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='info' onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderEdit = () => {
    return (
      <Dialog open={modalEdit} maxWidth='xs' keepMounted aria-describedby='dialog-edit' fullWidth>
        <DialogTitle>
          {t(`element.edit`)} #{attr.id}
          <IconButton
            aria-label='close'
            onClick={() => setModalEdit(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={edit}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='success' onClick={editConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderDelete = () => {
    return (
      <Dialog
        open={modalDel}
        onClose={() => setModalDel(false)}
        maxWidth='xs'
        keepMounted
        aria-describedby='dialog-delete'
        fullWidth
      >
        <DialogTitle>
          {t(`element.delete`)} #{attr.id}
          <IconButton
            aria-label='close'
            onClick={() => setModalDel(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText p={3}>{t(`alert.confirm_delete_title`)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setModalDel(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='gradient' color='error' onClick={del}>
                  <Icon>delete</Icon>&nbsp;{t(`element.deleteconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='error' onClick={() => setConfirm(true)}>
              <Icon>delete</Icon>&nbsp;{t(`element.delete`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderPrint = () => {
    return (
      <Dialog
        open={modalPrint}
        maxWidth='xs'
        keepMounted
        aria-describedby='dialog-print'
        fullWidth
        onClose={() => setModalPrint(false)}
      >
        <DialogTitle>
          #{attr.code}
          <IconButton
            aria-label='close'
            onClick={() => setModalPrint(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {attr.print === '' ? null : (
            <Iframe
              url={attr.print}
              width='100%'
              height='300'
              id='print'
              className=''
              display='initial'
              position='relative'
              frameBorder='0'
            />
          )}
        </DialogContent>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.warehouse`), t(`menu.location`)]} />
      {renderList()}
      {renderAdd()}
      {renderEdit()}
      {renderPrint()}
      {renderDelete()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default Location;
